/**
 * Mastery showcase — the spec §8 "long list" rethink.
 *
 * Mastered words accumulate without bound (one sticker per word) and every
 * emblem is the same hexagon template, so the old flat grid grew into a
 * wall of identical shapes differing only by caption. This presents the
 * achievement as a single large COUNT, with the individual word-stickers
 * tucked into a horizontally swipeable carousel you flip through rather
 * than scroll past.
 *
 * Newest masters lead, so a freshly-earned word is the first thing Eva
 * swipes to. Paging is clientWidth-based: each arrow tap / dot moves one
 * screenful, which keeps the dot count sane no matter how many words pile
 * up. Controls hide entirely when everything already fits on screen.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { ExplainableSticker } from "@/components/ExplainableSticker";
import type { Achievement } from "@/types/data";
import "./MasteryCarousel.css";

export function MasteryCarousel({ items }: { items: Achievement[] }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState(1);
  const [active, setActive] = useState(0);

  // Newest first. unlockedAt is an ISO string, so a lexicographic compare
  // is the same as a chronological one (nulls sort last, but mastery rows
  // are only inserted on unlock so there shouldn't be any).
  const ordered = [...items].sort((a, b) =>
    (b.unlockedAt ?? "").localeCompare(a.unlockedAt ?? ""),
  );

  const recompute = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const cw = el.clientWidth || 1;
    setPages(Math.max(1, Math.ceil(el.scrollWidth / cw)));
    setActive(Math.round(el.scrollLeft / cw));
  }, []);

  useEffect(() => {
    recompute();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", recompute, { passive: true });
    window.addEventListener("resize", recompute);
    return () => {
      el.removeEventListener("scroll", recompute);
      window.removeEventListener("resize", recompute);
    };
    // items.length: re-measure when the collection grows.
  }, [recompute, items.length]);

  const page = (dir: -1 | 1) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth });
  };

  const count = items.length;
  const showControls = pages > 1;

  return (
    <div className="mastery">
      <div className="mastery__summary">
        <div className="mastery__count">{count}</div>
        <div className="mastery__summary-text">
          <span className="mastery__count-label">
            {count === 1 ? "word mastered" : "words mastered"}
          </span>
          {showControls && (
            <span className="mastery__hint">Swipe to see them all</span>
          )}
        </div>
      </div>

      <div className="mastery__carousel">
        <div
          className="mastery__track"
          ref={trackRef}
          role="list"
          aria-label={`${count} mastered words`}
        >
          {ordered.map((a) => (
            <div className="mastery__item" role="listitem" key={a.id}>
              <ExplainableSticker
                category="mastery"
                stickerId={a.id}
                name={a.name}
                isNew={!a.seenByUser}
              />
            </div>
          ))}
        </div>

        {showControls && (
          <div className="mastery__controls">
            <button
              type="button"
              className="mastery__arrow"
              onClick={() => page(-1)}
              disabled={active <= 0}
              aria-label="Previous words"
            >
              <Chevron dir="left" />
            </button>
            <div className="mastery__dots" aria-hidden="true">
              {Array.from({ length: pages }, (_, i) => (
                <span
                  key={i}
                  className={"mastery__dot" + (i === active ? " is-active" : "")}
                />
              ))}
            </div>
            <button
              type="button"
              className="mastery__arrow"
              onClick={() => page(1)}
              disabled={active >= pages - 1}
              aria-label="More words"
            >
              <Chevron dir="right" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Chevron({ dir }: { dir: "left" | "right" }) {
  const d = dir === "left" ? "M15 6 L9 12 L15 18" : "M9 6 L15 12 L9 18";
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
